// Real, live config for this repo's actual Firebase project (zenfresh-c97a1)
// — a Firebase web app's config is not a secret (unlike an API server key);
// it identifies the project so the client SDK knows where to connect, and
// access is enforced entirely by firestore.rules, not by hiding this
// object. Fetched directly via `firebase apps:sdkconfig web`, not guessed.
export const firebaseConfig = {
  projectId: "zenfresh-c97a1",
  appId: "1:1011175130063:web:b31259e7adc933c4d7c8ba",
  storageBucket: "zenfresh-c97a1.firebasestorage.app",
  apiKey: "AIzaSyD897vcAn2IW9pmHemk9HN7TwUED2FalRE",
  authDomain: "zenfresh-c97a1.firebaseapp.com",
  messagingSenderId: "1011175130063",
};

const SESSION_ID_KEY = "zenfresh-session-id";

// Random per browser-session, not persisted across sessions or tied to any
// real identity — this is aggregate traffic monitoring, not user tracking.
function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

// firebase/app + firebase/firestore are only pulled in when a consenting
// visitor actually triggers a trackable event (see analytics.ts) — lazy,
// not a top-level import, matching this project's established lazy-loading
// discipline for every other non-trivial dependency (three.js, jszip,
// @react-pdf/renderer).
let dbPromise: ReturnType<typeof initFirestore> | null = null;

async function initFirestore() {
  const [{ getApps, initializeApp }, { getFirestore }] = await Promise.all([
    import("firebase/app"),
    import("firebase/firestore"),
  ]);
  const app = getApps()[0] ?? initializeApp(firebaseConfig);
  return getFirestore(app);
}

/**
 * Fires a write-only, heavily-validated event document to the
 * `traffic_events` Firestore collection (see firestore.rules — no client
 * can ever read this collection back, only create new documents matching
 * a strict schema). Gated on cookie consent by its only caller
 * (analytics.ts's trackEvent), same as the GA4 path. Never throws — an
 * analytics failure must never break the page or surface to the visitor.
 */
export async function logTrafficEvent(eventType: string, params: Record<string, string | number | boolean> = {}) {
  try {
    if (!dbPromise) dbPromise = initFirestore();
    const db = await dbPromise;
    const { collection, addDoc, serverTimestamp } = await import("firebase/firestore");

    // Values stringified so the Firestore rule's schema check doesn't need
    // to validate a nested map's per-key value types.
    const stringParams = Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]));

    await addDoc(collection(db, "traffic_events"), {
      eventType,
      path: window.location.pathname,
      sessionId: getSessionId(),
      timestamp: serverTimestamp(),
      params: stringParams,
    });
  } catch {
    // Network hiccup, ad-blocker, offline, etc. — silently drop the event.
  }
}

export interface OrderLineItem {
  id: string;
  name: string;
  unitPrice: number;
  qty: number;
  lineTotal: number;
}

export type OrderStatus = "payment_reported_unverified" | "payment_failed" | "payment_cancelled";

export interface OrderContact {
  name: string;
  phone: string;
  email: string;
  addressLine: string;
  city: string;
  pincode: string;
}

export interface OrderInput {
  orderRef: string;
  status: OrderStatus;
  items: OrderLineItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  paymentId: string | null;
  contact: OrderContact;
}

export interface OrderSummary {
  orderRef: string;
  status: OrderStatus;
  total: number;
  items: OrderLineItem[];
}

/**
 * Writes the public order doc and its PII-bearing `private/contact`
 * subdoc in one atomic batch, doc ID == orderRef (see firestore.rules —
 * this is what lets getOrderByReference do a plain `get` with no `list`).
 * Fires only once, after a Razorpay Checkout flow concludes (success,
 * failed, or cancelled are all written honestly — see
 * src/utils/razorpayCheckout.ts) — never before the flow opens.
 */
export async function writeOrder(order: OrderInput): Promise<void> {
  if (!dbPromise) dbPromise = initFirestore();
  const db = await dbPromise;
  const { doc, collection, writeBatch, serverTimestamp } = await import("firebase/firestore");

  const orderRefDoc = doc(collection(db, "orders"), order.orderRef);
  const contactRefDoc = doc(collection(orderRefDoc, "private"), "contact");

  const batch = writeBatch(db);
  batch.set(orderRefDoc, {
    orderRef: order.orderRef,
    createdAt: serverTimestamp(),
    status: order.status,
    items: order.items,
    subtotal: order.subtotal,
    deliveryFee: order.deliveryFee,
    total: order.total,
    razorpay: { paymentId: order.paymentId },
  });
  batch.set(contactRefDoc, order.contact);
  await batch.commit();
}

const ORDER_STATUSES: OrderStatus[] = ["payment_reported_unverified", "payment_failed", "payment_cancelled"];

/**
 * Firestore data is externally-sourced from this function's own type
 * system's point of view (rules constrain writes, but don't guarantee
 * `snap.data()` matches OrderSummary at compile time) — validated the same
 * way cartStore.ts's readCart() validates localStorage before trusting it,
 * rather than casting straight through, which would let a future malformed
 * doc (e.g. a manual console edit) crash the chatbot's `.toLocaleString()`
 * call on a non-number `total` instead of failing this one lookup cleanly.
 */
function isOrderSummary(data: unknown): data is OrderSummary {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.orderRef === "string" &&
    typeof d.status === "string" &&
    ORDER_STATUSES.includes(d.status as OrderStatus) &&
    typeof d.total === "number" &&
    Array.isArray(d.items)
  );
}

/**
 * Used by the chatbot's order-status lookup (see
 * src/hooks/useChatbotEngine.ts). Reads only the public top-level doc —
 * never the private/contact subcollection, which these rules deny to every
 * client anyway. Never throws: not-found, a malformed doc, and any
 * transient error all resolve to null so the chatbot can give one honest
 * "couldn't find that order" answer either way.
 */
export async function getOrderByReference(orderRef: string): Promise<OrderSummary | null> {
  try {
    if (!dbPromise) dbPromise = initFirestore();
    const db = await dbPromise;
    const { doc, getDoc } = await import("firebase/firestore");

    const snap = await getDoc(doc(db, "orders", orderRef));
    if (!snap.exists()) return null;

    const data = snap.data();
    return isOrderSummary(data) ? data : null;
  } catch {
    return null;
  }
}
